import { useEditor, EditorContent } from '@tiptap/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createEditorActions } from './ContentEditor.actions';
import { createEditorConfig } from './ContentEditor.config';
import styles from './ContentEditor.module.scss';
import { ContentEditorToolbar } from './ContentEditorToolbar';

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  editable?: boolean;
};

export const ContentEditor = ({ value, onChange, placeholder = '', editable = true }: Props) => {
  const { t } = useTranslation();
  const [isEmpty, setIsEmpty] = useState(!value || value === '<p></p>' || value === '');

  const config = createEditorConfig(placeholder);

  const editor = useEditor({
    ...config,
    editable,
    content: isEmpty ? placeholder : value,
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      const contentIsEmpty = !content || content === '<p></p>';

      if (contentIsEmpty) {
        onChange(placeholder);
        setIsEmpty(true);
      } else {
        onChange(content);
        setIsEmpty(false);
      }
    },
    onFocus: ({ editor }) => {
      if (isEmpty) {
        editor.commands.setContent('');
      }
    },
    onBlur: ({ editor }) => {
      if (editor.isEmpty) {
        editor.commands.setContent(placeholder);
        setIsEmpty(true);
        onChange(placeholder);
      }
    },
  });

  // Tiptap seeds `content` once, so a value that arrives after mount (a generated
  // translation, a late fetch) would otherwise never show. While the user types,
  // `value` is whatever onUpdate just sent up, so the compare below is a no-op and
  // the caret is never reset. Clearing is left to onUpdate/onBlur, which own the
  // placeholder dance.
  useEffect(() => {
    if (!editor || !value || value === '<p></p>' || value === placeholder) return;
    if (value === editor.getHTML()) return;
    setIsEmpty(false);
    editor.commands.setContent(value, false);
  }, [value, editor, placeholder]);

  const actions = editor ? createEditorActions(editor, t) : [];

  const handleEditorClick = () => {
    if (isEmpty && editor) {
      editor.commands.setContent('');
      setIsEmpty(false);
    }
  };

  return (
    <div className="p-0">
      <div id="hs-editor-tiptap">
        {editable && <ContentEditorToolbar actions={actions} />}
        <div
          className={classNames(
            'mt-4 min-h-[280px] w-full border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-0 p-0 focus-within:!ring-[3px] focus-within:ring-ring/25 focus-within:!border-foreground',
            { 'text-foreground-subtle': isEmpty },
          )}
        >
          <EditorContent
            editor={editor}
            className={classNames('h-full resize-none leading-3 text-sm', styles.richtextContent)}
            onClick={handleEditorClick}
          />
        </div>
      </div>
    </div>
  );
};
